/*
 * probe-descent.ts - the descent of the elevation pass
 */

import type ElevationStore from './elevation-store';
import type MapSurfaceTile from './surface-tile';
import type MapMetanode from './metanode';
import type ViewDescent from './view-descent';
import type { TerrainDescent } from './draw-traversal';


/**
 * The descent of the elevation pass. It is the view's descent plus the
 * probes, the registered sample positions.
 *
 * A tile that a probe within its extents needs, or needs something
 * finer than, is never culled, at any depth, on screen or off. It is
 * wanted finer when a probe within its extents needs a finer LOD, or
 * when the view sees it and wants it finer. A tile at the LOD a probe
 * within its extents needs fetches its resources: as a natural leaf if
 * nothing wants it finer, as a fallback otherwise. A tile finer than
 * every probe within its extents needs is left to the view, and when
 * the view culls it, its area is wanted covered, so the tile the probe
 * needs draws over it. Other tiles get the view's answers. No other
 * fallback fetches; coarser units come from the reduction of finer
 * ones.
 */
export class ProbeDescent implements TerrainDescent {

    /**
     * @param view the descent of the view the pass draws from
     * @param probeInterest the lookup of what the probes need of a
     *   tile, built by the store
     */
    constructor(
        view: ViewDescent,
        probeInterest: ElevationStore.ProbeInterestAt,
    ) {

        this.view = view;
        this.probeInterest = probeInterest;
    }

    culled(tile: MapSurfaceTile, node: MapMetanode): boolean {

        const interest = this.probeInterest(tile.id);

        if (interest.finer || interest.enough) return false;
        return this.view.culled(tile, node);
    }

    wantsCovered(tile: MapSurfaceTile, _node: MapMetanode): boolean {

        return this.probeInterest(tile.id).coarser;
    }

    wantsFiner(tile: MapSurfaceTile, node: MapMetanode): boolean {

        if (this.probeInterest(tile.id).finer) return true;

        // the view's interest, which it has in a tile it sees only
        return !this.view.culled(tile, node)
            && this.view.wantsFiner(tile, node);
    }

    fallbackFetches(tile: MapSurfaceTile): boolean {

        return this.probeInterest(tile.id).enough;
    }

    private readonly view: ViewDescent;
    private readonly probeInterest: ElevationStore.ProbeInterestAt;
}

export default ProbeDescent;
